import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Package, CheckCircle, XCircle, Clock, MapPin } from 'lucide-react';
import toast from 'react-hot-toast';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = async () => {
    try {
      const res = await api.get('/orders/seller');
      setOrders(res.data);
      setLoading(false);
    } catch (err) {
      toast.error("Orders load nahi ho sakay");
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const updateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}`, { status: newStatus });
      toast.success(`Order marked as ${newStatus}`);
      fetchOrders(); // List refresh karein
    } catch (err) {
      toast.error("Status update fail ho gaya");
    }
  };

  if (loading) return <div className="p-10 text-center font-black">Loading Orders...</div>;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-black text-gray-900">Orders Management</h2>
        <p className="text-gray-400 font-bold text-xs uppercase tracking-[0.2em] mt-2">Track and manage your sales</p>
      </div>

      <div className="grid gap-6">
        {orders.length > 0 ? orders.map((order) => (
          <div key={order._id} className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm flex flex-col md:flex-row justify-between gap-6">
            
            <div className="space-y-4 flex-1">
              <div className="flex items-center gap-3">
                <span className="px-4 py-1.5 bg-gray-100 rounded-full text-[10px] font-black font-mono tracking-widest text-gray-500">#{order._id.slice(-6).toUpperCase()}</span>
                <StatusBadge status={order.status} />
              </div>

              <div>
                {order.items.map((item, i) => (
                  <p key={i} className="text-gray-900 font-black text-lg">
                    {item.qty}x {item.name} <span className="text-gray-400 text-sm font-bold ml-2">Rs.{item.price * item.qty}</span>
                  </p>
                ))}
              </div>

              <div className="flex items-center gap-2 text-gray-400 text-xs font-bold uppercase tracking-wider">
                <MapPin size={14} className="text-green-500" />
                {order.location.substring(0, 40)}...
              </div>
            </div>

            <div className="flex flex-col justify-between items-end gap-4">
              <div className="text-right">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Grand Total</p>
                <h3 className="text-2xl font-black text-black leading-none mt-1">Rs. {order.totalAmount}</h3>
              </div>

              {/* Status Buttons */}
              <div className="flex gap-2">
                {order.status === 'pending' && (
                  <>
                    <button 
                      onClick={() => updateStatus(order._id, 'delivered')}
                      className="flex items-center gap-2 px-6 py-3 bg-green-500 text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-green-600 transition-all shadow-lg shadow-green-100"
                    >
                      <CheckCircle size={14}/> Delivered
                    </button>
                    <button 
                      onClick={() => updateStatus(order._id, 'cancelled')}
                      className="flex items-center gap-2 px-6 py-3 bg-red-50 text-red-500 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-red-100 transition-all"
                    >
                      <XCircle size={14}/> Cancel
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        )) : (
          <div className="text-center py-20 bg-white rounded-[3rem] border border-dashed border-gray-200">
             <Package className="mx-auto text-gray-200 mb-4" size={48} />
             <p className="text-gray-400 font-bold uppercase tracking-widest text-sm">No orders found yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-orange-50 text-orange-500",
    delivered: "bg-green-50 text-green-600",
    cancelled: "bg-red-50 text-red-500"
  };
  return (
    <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-tighter ${styles[status]}`}>
      {status}
    </span>
  );
};

export default OrdersPage;